// src/components/admin/employees/EmployeeActionPanels.js
import React, { useState, useEffect } from 'react';
import { X, Bold, Italic, List, ListOrdered, Paperclip, Info, AlertCircle, CheckCircle, Edit2, XCircle as CancelIcon, Ban, IndianRupee } from 'lucide-react';
import exitService from '../../../services/exit.service';
import employeeService from '../../../services/employee.service';
import payrollService from '../../../services/payroll.service';
import { useEmployees } from '../../../contexts/EmployeeContext';

const EmployeeActionPanels = ({ employee, activeSidePanel, onClose }) => {
    const { fetchEmployees } = useEmployees();
    const [formData, setFormData] = useState({
        praise: '',
        badge: '',
        postToWall: false,
        department: '',
        internalNote: '',
        feedbackText: '',
        feedbackTo: [],
        exitDate: '',
        exitReason: '',
        exitType: 'resignation',
        resignationDate: '',
        exitComments: '',
        newPassword: '',
        showPassword: false
    });

    const [attachments, setAttachments] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [validationWarnings, setValidationWarnings] = useState([]);
    const [exitRequest, setExitRequest] = useState(null);
    const [editAction, setEditAction] = useState(null); // 'modify' or 'cancel'

    // ── Withhold salary state ─────────────────────────────────────
    const today = new Date();
    const [holdMonth, setHoldMonth] = useState(today.getMonth() + 1); // 1-12
    const [holdYear, setHoldYear] = useState(today.getFullYear());
    const [holdReason, setHoldReason] = useState('');
    const [activeHolds, setActiveHolds] = useState([]); // existing un-released holds for this employee
    const [holdSuccess, setHoldSuccess] = useState(null);

    useEffect(() => {
        const load = async () => {
            if (activeSidePanel !== 'withhold-salary' || !employee?.id) return;
            try {
                const res = await payrollService.listPeriodHoldsForEmployee(employee.id);
                const all = res?.holds || [];
                setActiveHolds(all.filter((h) => !h.released_at));
            } catch (e) {
                console.warn('Could not load existing holds:', e?.message);
            }
        };
        load();
    }, [activeSidePanel, employee?.id, holdSuccess]);

    // Fetch exit request when edit-exit panel opens
    useEffect(() => {
        if (activeSidePanel === 'edit-exit' && employee?.id) {
            fetchExitRequest();
        }
    }, [activeSidePanel, employee?.id]);

    const fetchExitRequest = async () => {
        try {
            const response = await exitService.getExitByEmployee(employee.id);
            if (response.success) {
                setExitRequest(response.data);
                // Pre-fill form data
                setFormData({
                    ...formData,
                    resignationDate: response.data.resignation_date,
                    exitDate: response.data.requested_exit_date,
                    exitType: response.data.exit_type,
                    exitReason: response.data.exit_reason,
                    exitComments: response.data.additional_comments || ''
                });
            } else {
                // No active exit request found
                setSubmitError('No active exit request found. The exit may have been cancelled or completed.');
                setExitRequest({ notFound: true });
            }
        } catch (error) {
            console.error('Error fetching exit request:', error);
            setSubmitError('Failed to load exit request details. The exit may have been cancelled.');
            setExitRequest({ notFound: true });
        }
    };

    const handleCancelExit = async () => {
        if (!window.confirm('Are you sure you want to cancel this exit request? The employee will be marked as Active again.')) {
            return;
        }

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            const reason = prompt('Please provide a reason for cancelling (optional):');
            console.log('Cancelling exit request:', exitRequest.id, 'Reason:', reason);

            const response = await exitService.cancelExit(exitRequest.id, reason);
            console.log('Cancel exit response:', response);

            if (response.success) {
                console.log('Exit cancelled successfully, refreshing employee data...');
                // Refresh employee data to reflect status change
                await fetchEmployees(true);
                alert('Exit request cancelled successfully. Employee status changed to Active.');
                onClose();
            } else {
                console.error('Cancel failed:', response.message);
                setSubmitError(response.message || 'Failed to cancel exit request');
            }
        } catch (error) {
            console.error('Error cancelling exit:', error);
            setSubmitError('An error occurred while cancelling exit');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleModifyExit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);

        try {
            // Validate dates
            const resignDate = new Date(formData.resignationDate);
            const exitDate = new Date(formData.exitDate);

            if (resignDate > exitDate) {
                setSubmitError('Resignation date cannot be after exit date');
                setIsSubmitting(false);
                return;
            }

            const reason = prompt('Please provide a reason for modifying the dates (optional):');

            const response = await exitService.updateExitDates(exitRequest.id, {
                resignation_date: formData.resignationDate,
                requested_exit_date: formData.exitDate,
                update_reason: reason
            });

            if (response.success) {
                // Refresh employee data to reflect updated dates
                await fetchEmployees(true);
                alert('Exit dates updated successfully!');
                onClose();
            } else {
                setSubmitError(response.message || 'Failed to update exit dates');
            }
        } catch (error) {
            console.error('Error modifying exit:', error);
            setSubmitError('An error occurred while updating exit dates');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);
        setValidationWarnings([]);

        try {
            if (activeSidePanel === 'initiate-exit') {
                // Validate exit data
                if (!formData.exitDate || !formData.exitReason || !formData.resignationDate) {
                    setSubmitError('Please fill in all required fields');
                    setIsSubmitting(false);
                    return;
                }

                // Validate dates
                const resignDate = new Date(formData.resignationDate);
                const exitDate = new Date(formData.exitDate);
                const joiningDate = new Date(employee.joining_date || employee.joiningDate);

                if (exitDate <= joiningDate) {
                    setSubmitError('Exit date must be after joining date');
                    setIsSubmitting(false);
                    return;
                }

                if (resignDate > exitDate) {
                    setSubmitError('Resignation date cannot be after exit date');
                    setIsSubmitting(false);
                    return;
                }

                // Call exit service
                const exitData = {
                    employee_id: employee.id,
                    exit_type: formData.exitType,
                    exit_reason: formData.exitReason,
                    requested_exit_date: formData.exitDate,
                    resignation_date: formData.resignationDate,
                    additional_comments: formData.exitComments || '',
                    notice_period_buyout: false
                };

                const response = await exitService.initiateExit(exitData);

                if (response.success) {
                    // Refresh employee data to reflect notice period status
                    await fetchEmployees(true);

                    // Show validation warnings if any
                    if (response.data.validations.warnings && response.data.validations.warnings.length > 0) {
                        setValidationWarnings(response.data.validations.warnings);
                        alert(`Exit request initiated successfully!\n\nWarnings:\n${response.data.validations.warnings.map(w => `- ${w.message}`).join('\n')}`);
                    } else {
                        alert('Exit request initiated successfully!');
                    }
                    onClose();
                } else {
                    setSubmitError(response.message || 'Failed to initiate exit request');
                }
            } else if (activeSidePanel === 'disable-login' || activeSidePanel === 'enable-login') {
                // Toggle login access
                const shouldDisable = activeSidePanel === 'disable-login';
                const response = await employeeService.toggleLoginAccess(employee.id, shouldDisable);

                if (response.success) {
                    await fetchEmployees(true);
                    alert(`Login access ${shouldDisable ? 'disabled' : 'enabled'} successfully for ${employee.first_name} ${employee.last_name}`);
                    onClose();
                } else {
                    setSubmitError(response.message || 'Failed to toggle login access');
                }
            } else if (activeSidePanel === 'reset-password') {
                // Admin reset password
                if (!formData.newPassword || formData.newPassword.length < 6) {
                    setSubmitError('Password must be at least 6 characters long');
                    setIsSubmitting(false);
                    return;
                }

                const response = await employeeService.adminResetPassword(employee.id, formData.newPassword);

                if (response.success) {
                    alert(`Password reset successfully for ${employee.first_name} ${employee.last_name}`);
                    setFormData({ ...formData, newPassword: '' });
                    onClose();
                } else {
                    setSubmitError(response.message || 'Failed to reset password');
                }
            } else {
                // Other form submissions (praise, feedback, etc.)
                console.log('Submitting:', activeSidePanel, formData);
                // TODO: Implement other submission logic
                alert('Feature coming soon!');
                onClose();
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitError(error.message || 'An error occurred while submitting');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!activeSidePanel) return null;

    const renderPanel = () => {
        switch (activeSidePanel) {
            case 'praise':
                return (
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Give Praise to {employee?.first_name} {employee?.last_name}
                            </h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Employee Info */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                                    {employee?.first_name?.[0]}{employee?.last_name?.[0]}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {employee?.first_name} {employee?.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {employee?.designation}
                                    </div>
                                </div>
                            </div>

                            {/* Praise Input */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Enter your praise
                                </label>
                                <div className="border dark:border-gray-600 rounded-lg overflow-hidden">
                                    {/* Toolbar */}
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
                                        <select className="text-sm px-2 py-1 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:text-white">
                                            <option>Normal</option>
                                            <option>Heading 1</option>
                                            <option>Heading 2</option>
                                        </select>
                                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                                            <Bold className="w-4 h-4" />
                                        </button>
                                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                                            <Italic className="w-4 h-4" />
                                        </button>
                                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                                            <List className="w-4 h-4" />
                                        </button>
                                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                                            <ListOrdered className="w-4 h-4" />
                                        </button>
                                    </div>
                                    {/* Text Area */}
                                    <textarea
                                        value={formData.praise}
                                        onChange={(e) => setFormData({ ...formData, praise: e.target.value })}
                                        placeholder="Ex: You are doing great!"
                                        className="w-full p-3 min-h-[120px] resize-none bg-white dark:bg-gray-800 dark:text-white focus:outline-none"
                                    />
                                </div>
                                <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    <Info className="w-4 h-4" />
                                    <span>Praise will be visible to everyone in the organization.</span>
                                </div>
                            </div>

                            {/* Select Badge */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Select a Badge
                                </label>
                                <select
                                    value={formData.badge}
                                    onChange={(e) => setFormData({ ...formData, badge: e.target.value })}
                                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                >
                                    <option value="">Select a Badge</option>
                                    <option value="star-performer">⭐ Star Performer</option>
                                    <option value="team-player">🤝 Team Player</option>
                                    <option value="innovative">💡 Innovative Thinker</option>
                                    <option value="leadership">👑 Leadership</option>
                                </select>
                            </div>

                            {/* Post to Wall */}
                            <div className="mb-4 flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="postToWall"
                                    checked={formData.postToWall}
                                    onChange={(e) => setFormData({ ...formData, postToWall: e.target.checked })}
                                    className="w-4 h-4"
                                />
                                <label htmlFor="postToWall" className="text-sm text-gray-700 dark:text-gray-300">
                                    Post to wall
                                </label>
                                {formData.postToWall && (
                                    <select
                                        value={formData.department}
                                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                        className="ml-auto px-3 py-1 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:text-white text-sm"
                                    >
                                        <option value="">Select Department</option>
                                        <option value="all">All Departments</option>
                                        <option value="hr">HR</option>
                                        <option value="it">IT</option>
                                        <option value="sales">Sales</option>
                                    </select>
                                )}
                            </div>

                            {/* Add Attachment */}
                            <div className="mb-4">
                                <button className="text-blue-600 dark:text-blue-400 text-sm flex items-center gap-2 hover:underline">
                                    <Paperclip className="w-4 h-4" />
                                    Add Attachment
                                </button>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Max number of files allowed is 5
                                </p>
                            </div>
                        </div>

                        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-2">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                );

            case 'feedback':
                return (
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Give Personal Feedback to {employee?.first_name} {employee?.last_name}
                            </h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Employee Info */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                                    {employee?.first_name?.[0]}{employee?.last_name?.[0]}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {employee?.first_name} {employee?.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {employee?.designation}
                                    </div>
                                </div>
                            </div>

                            {/* Feedback Input */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Enter your feedback
                                </label>
                                <textarea
                                    value={formData.feedbackText}
                                    onChange={(e) => setFormData({ ...formData, feedbackText: e.target.value })}
                                    placeholder="Enter feedback..."
                                    className="w-full p-3 min-h-[150px] resize-none border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                                <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    <Info className="w-4 h-4" />
                                    <span>Feedback will be visible only to the employee.</span>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-2">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                );

            case 'internal-note':
                return (
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Give Internal Note to {employee?.first_name} {employee?.last_name}
                            </h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Employee Info */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center text-white font-semibold">
                                    {employee?.first_name?.[0]}{employee?.last_name?.[0]}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {employee?.first_name} {employee?.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {employee?.designation}
                                    </div>
                                </div>
                            </div>

                            {/* Internal Note Input */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Add internal note
                                </label>
                                <div className="border dark:border-gray-600 rounded-lg overflow-hidden">
                                    {/* Toolbar */}
                                    <div className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
                                        <select className="text-sm px-2 py-1 border dark:border-gray-600 rounded bg-white dark:bg-gray-800 dark:text-white">
                                            <option>Normal</option>
                                        </select>
                                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                                            <Bold className="w-4 h-4" />
                                        </button>
                                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                                            <Italic className="w-4 h-4" />
                                        </button>
                                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                                            <List className="w-4 h-4" />
                                        </button>
                                        <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                                            <ListOrdered className="w-4 h-4" />
                                        </button>
                                    </div>
                                    {/* Text Area */}
                                    <textarea
                                        value={formData.internalNote}
                                        onChange={(e) => setFormData({ ...formData, internalNote: e.target.value })}
                                        placeholder="Enter Internal note"
                                        className="w-full p-3 min-h-[150px] resize-none bg-white dark:bg-gray-800 dark:text-white focus:outline-none"
                                    />
                                </div>
                                <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    <Info className="w-4 h-4" />
                                    <span>Internal note will only be visible to managers</span>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-2">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                );

            case 'request-feedback':
                return (
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Request Feedback for {employee?.first_name} {employee?.last_name}
                            </h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Employee Info */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                                    {employee?.first_name?.[0]}{employee?.last_name?.[0]}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {employee?.first_name} {employee?.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {employee?.designation}
                                    </div>
                                </div>
                            </div>

                            {/* Request feedback from */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Request feedback from
                                </label>
                                <select
                                    multiple
                                    value={formData.feedbackTo}
                                    onChange={(e) => setFormData({ ...formData, feedbackTo: Array.from(e.target.selectedOptions, option => option.value) })}
                                    className="w-full px-3 py-2 min-h-[100px] border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                >
                                    <option value="manager">Direct Manager</option>
                                    <option value="team-member-1">Team Member 1</option>
                                    <option value="team-member-2">Team Member 2</option>
                                    <option value="peer-1">Peer 1</option>
                                </select>
                                <div className="flex items-center gap-2 mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    <Info className="w-4 h-4" />
                                    <span>Select one or more people to request feedback from</span>
                                </div>
                            </div>

                            {/* Feedback message */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Message (optional)
                                </label>
                                <textarea
                                    value={formData.feedbackText}
                                    onChange={(e) => setFormData({ ...formData, feedbackText: e.target.value })}
                                    placeholder="Add a message to go along with your request..."
                                    className="w-full p-3 min-h-[120px] resize-none border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-2">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                                Send Request
                            </button>
                        </div>
                    </div>
                );

            case 'initiate-exit':
                return (
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Initiate Exit for {employee?.first_name} {employee?.last_name}
                            </h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" disabled={isSubmitting}>
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Warning */}
                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
                                <div className="flex items-start gap-3">
                                    <div className="text-yellow-600 dark:text-yellow-400 text-xl">⚠️</div>
                                    <div>
                                        <h3 className="font-semibold text-yellow-900 dark:text-yellow-300">Warning: Exit Process</h3>
                                        <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                                            This will initiate the exit process for the employee. This action should be taken carefully.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Error Message */}
                            {submitError && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                        <div>
                                            <h3 className="font-semibold text-red-900 dark:text-red-300">Error</h3>
                                            <p className="text-sm text-red-700 dark:text-red-400 mt-1">{submitError}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Validation Warnings */}
                            {validationWarnings.length > 0 && (
                                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-4">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-orange-900 dark:text-orange-300">Warnings Detected</h3>
                                            <ul className="text-sm text-orange-700 dark:text-orange-400 mt-2 space-y-1">
                                                {validationWarnings.map((warning, idx) => (
                                                    <li key={idx}>• {warning.message}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Employee Info */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center text-white font-semibold">
                                    {employee?.first_name?.[0]}{employee?.last_name?.[0]}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {employee?.first_name} {employee?.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {employee?.designation} • Joined: {employee?.joining_date || employee?.joiningDate}
                                    </div>
                                </div>
                            </div>

                            {/* Exit Type */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Exit Type <span className="text-red-500">*</span>
                                </label>
                                <select
                                    value={formData.exitType}
                                    onChange={(e) => setFormData({ ...formData, exitType: e.target.value })}
                                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                    disabled={isSubmitting}
                                >
                                    <option value="resignation">Resignation</option>
                                    <option value="termination">Termination</option>
                                    <option value="retirement">Retirement</option>
                                    <option value="end_of_contract">End of Contract</option>
                                    <option value="mutual_separation">Mutual Separation</option>
                                    <option value="absconding">Absconding</option>
                                </select>
                            </div>

                            {/* Resignation Date */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Resignation Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={formData.resignationDate}
                                    onChange={(e) => setFormData({ ...formData, resignationDate: e.target.value })}
                                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                    disabled={isSubmitting}
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Date when the employee submitted resignation
                                </p>
                            </div>

                            {/* Exit Date */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Requested Last Working Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={formData.exitDate}
                                    onChange={(e) => setFormData({ ...formData, exitDate: e.target.value })}
                                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                    disabled={isSubmitting}
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Must be after joining date. Notice period will be validated.
                                </p>
                            </div>

                            {/* Exit Reason */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Reason for Exit <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={formData.exitReason}
                                    onChange={(e) => setFormData({ ...formData, exitReason: e.target.value })}
                                    placeholder="Provide detailed reason for exit..."
                                    className="w-full p-3 min-h-[100px] resize-none border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:outline-none"
                                    disabled={isSubmitting}
                                />
                            </div>

                            {/* Additional Comments */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Additional Comments (Optional)
                                </label>
                                <textarea
                                    value={formData.exitComments}
                                    onChange={(e) => setFormData({ ...formData, exitComments: e.target.value })}
                                    placeholder="Any additional information..."
                                    className="w-full p-3 min-h-[80px] resize-none border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:outline-none"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>

                        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-2">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 disabled:opacity-50"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Processing...</span>
                                    </>
                                ) : (
                                    'Initiate Exit Process'
                                )}
                            </button>
                        </div>
                    </div>
                );

            case 'edit-exit':
                return (
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Edit Exit for {employee?.first_name} {employee?.last_name}
                            </h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" disabled={isSubmitting}>
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Info Banner */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                                <div className="flex items-start gap-3">
                                    <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                                    <div>
                                        <h3 className="font-semibold text-blue-900 dark:text-blue-300">Notice Period Active</h3>
                                        <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                                            This employee is currently in notice period. You can cancel the exit or modify the dates.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Error Message */}
                            {submitError && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                        <div>
                                            <h3 className="font-semibold text-red-900 dark:text-red-300">Error</h3>
                                            <p className="text-sm text-red-700 dark:text-red-400 mt-1">{submitError}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {!exitRequest ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading exit details...</span>
                                </div>
                            ) : editAction === null ? (
                                /* Action Selection */
                                <div className="space-y-4">
                                    <h3 className="font-medium text-gray-900 dark:text-white mb-4">Choose an action:</h3>

                                    <button
                                        onClick={() => setEditAction('modify')}
                                        className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-left transition-colors"
                                    >
                                        <div className="flex items-start gap-3">
                                            <Edit2 className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1" />
                                            <div className="flex-1">
                                                <div className="font-semibold text-gray-900 dark:text-white">Modify Exit Dates</div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                    Change resignation date or last working date
                                                </div>
                                            </div>
                                        </div>
                                    </button>

                                    <button
                                        onClick={() => setEditAction('cancel')}
                                        className="w-full p-4 border-2 border-gray-200 dark:border-gray-700 rounded-lg hover:border-red-500 dark:hover:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 text-left transition-colors"
                                    >
                                        <div className="flex items-start gap-3">
                                            <CancelIcon className="w-6 h-6 text-red-600 dark:text-red-400 mt-1" />
                                            <div className="flex-1">
                                                <div className="font-semibold text-gray-900 dark:text-white">Cancel Exit Process</div>
                                                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                                    Revert employee back to Active status
                                                </div>
                                            </div>
                                        </div>
                                    </button>

                                    {/* Current Exit Info */}
                                    <div className="mt-6 pt-6 border-t dark:border-gray-700">
                                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">Current Exit Details:</h4>
                                        <div className="space-y-2 text-sm">
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 dark:text-gray-400">Exit Type:</span>
                                                <span className="text-gray-900 dark:text-white font-medium">{exitRequest.exit_type}</span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 dark:text-gray-400">Resignation Date:</span>
                                                <span className="text-gray-900 dark:text-white font-medium">
                                                    {new Date(exitRequest.resignation_date).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 dark:text-gray-400">Last Working Date:</span>
                                                <span className="text-gray-900 dark:text-white font-medium">
                                                    {new Date(exitRequest.requested_exit_date).toLocaleDateString()}
                                                </span>
                                            </div>
                                            <div className="flex justify-between">
                                                <span className="text-gray-500 dark:text-gray-400">Notice Period:</span>
                                                <span className="text-gray-900 dark:text-white font-medium">{exitRequest.notice_period_days} days</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : editAction === 'modify' ? (
                                /* Modify Form */
                                <div className="space-y-4">
                                    <button
                                        onClick={() => setEditAction(null)}
                                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4"
                                    >
                                        ← Back to options
                                    </button>

                                    <h3 className="font-medium text-gray-900 dark:text-white">Modify Exit Dates</h3>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Resignation Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.resignationDate}
                                            onChange={(e) => setFormData({ ...formData, resignationDate: e.target.value })}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                            disabled={isSubmitting}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Last Working Date *
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.exitDate}
                                            onChange={(e) => setFormData({ ...formData, exitDate: e.target.value })}
                                            className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                            disabled={isSubmitting}
                                        />
                                    </div>

                                    <div className="flex gap-2 pt-4">
                                        <button
                                            onClick={() => setEditAction(null)}
                                            className="flex-1 px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleModifyExit}
                                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Updating...' : 'Update Dates'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                /* Cancel Confirmation */
                                <div className="space-y-4">
                                    <button
                                        onClick={() => setEditAction(null)}
                                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4"
                                    >
                                        ← Back to options
                                    </button>

                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                        <h3 className="font-semibold text-red-900 dark:text-red-300">Confirm Cancellation</h3>
                                        <p className="text-sm text-red-700 dark:text-red-400 mt-2">
                                            This will:
                                        </p>
                                        <ul className="text-sm text-red-700 dark:text-red-400 mt-2 ml-4 list-disc space-y-1">
                                            <li>Cancel the exit request</li>
                                            <li>Change employee status back to <strong>Active</strong></li>
                                            <li>Remove notice period status</li>
                                        </ul>
                                    </div>

                                    <div className="flex gap-2 pt-4">
                                        <button
                                            onClick={() => setEditAction(null)}
                                            className="flex-1 px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                            disabled={isSubmitting}
                                        >
                                            Go Back
                                        </button>
                                        <button
                                            onClick={handleCancelExit}
                                            className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Cancelling...' : 'Confirm Cancel Exit'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );

            case 'disable-login':
            case 'enable-login':
                const isCurrentlyDisabled = employee?.login_disabled === true;
                const actionIsDisable = activeSidePanel === 'disable-login';

                return (
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {actionIsDisable ? 'Disable' : 'Enable'} Login for {employee?.first_name} {employee?.last_name}
                            </h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Warning/Info */}
                            <div className={`${actionIsDisable ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800' : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'} border rounded-lg p-4 mb-6`}>
                                <div className="flex items-start gap-3">
                                    <div className={`${actionIsDisable ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'} text-xl`}>
                                        {actionIsDisable ? '🚫' : '✅'}
                                    </div>
                                    <div>
                                        <h3 className={`font-semibold ${actionIsDisable ? 'text-red-900 dark:text-red-300' : 'text-green-900 dark:text-green-300'}`}>
                                            {actionIsDisable ? 'Warning: Access Restriction' : 'Enable Access'}
                                        </h3>
                                        <p className={`text-sm ${actionIsDisable ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'} mt-1`}>
                                            {actionIsDisable
                                                ? 'This will disable web portal login access for this employee. They will not be able to access the HR portal.'
                                                : 'This will enable web portal login access for this employee. They will be able to log in to the HR portal.'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Employee Info */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center text-white font-semibold">
                                    {employee?.first_name?.[0]}{employee?.last_name?.[0]}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {employee?.first_name} {employee?.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {employee?.designation}
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Current Status: {' '}
                                    <span className={`font-semibold ${isCurrentlyDisabled ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                        {isCurrentlyDisabled ? 'Login Disabled' : 'Login Enabled'}
                                    </span>
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Are you sure you want to {actionIsDisable ? 'disable' : 'enable'} login access for this employee?
                                </p>
                            </div>

                            {submitError && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 mb-4">
                                    <p className="text-sm text-red-700 dark:text-red-400">{submitError}</p>
                                </div>
                            )}
                        </div>

                        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-2">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                                className={`px-4 py-2 text-white rounded-lg disabled:opacity-50 ${
                                    actionIsDisable
                                        ? 'bg-red-600 hover:bg-red-700'
                                        : 'bg-green-600 hover:bg-green-700'
                                }`}
                            >
                                {isSubmitting ? 'Processing...' : actionIsDisable ? 'Disable Login' : 'Enable Login'}
                            </button>
                        </div>
                    </div>
                );

            case 'disable-mobile-login':
                return (
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Disable Mobile App Login for {employee?.first_name} {employee?.last_name}
                            </h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Warning */}
                            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
                                <div className="flex items-start gap-3">
                                    <div className="text-red-600 dark:text-red-400 text-xl">📱</div>
                                    <div>
                                        <h3 className="font-semibold text-red-900 dark:text-red-300">Warning: Mobile Access Restriction</h3>
                                        <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                                            This will disable mobile app login access for this employee. They will not be able to access the HR mobile app.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Employee Info */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center text-white font-semibold">
                                    {employee?.first_name?.[0]}{employee?.last_name?.[0]}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {employee?.first_name} {employee?.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {employee?.designation}
                                    </div>
                                </div>
                            </div>

                            <div className="mb-4">
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Current Status: <span className="font-semibold text-green-600 dark:text-green-400">Mobile Login Enabled</span>
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    Are you sure you want to disable mobile app login access for this employee?
                                </p>
                            </div>
                        </div>

                        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-2">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                            >
                                Disable Mobile Login
                            </button>
                        </div>
                    </div>
                );

            case 'reset-password':
                return (
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Reset Password for {employee?.first_name} {employee?.last_name}
                            </h2>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {/* Info */}
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                                <div className="flex items-start gap-3">
                                    <div className="text-blue-600 dark:text-blue-400 text-xl">🔑</div>
                                    <div>
                                        <h3 className="font-semibold text-blue-900 dark:text-blue-300">Admin Password Reset</h3>
                                        <p className="text-sm text-blue-700 dark:text-blue-400 mt-1">
                                            Set a new password for this employee. They should change it after their next login.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Employee Info */}
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
                                    {employee?.first_name?.[0]}{employee?.last_name?.[0]}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {employee?.first_name} {employee?.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {employee?.email}
                                    </div>
                                </div>
                            </div>

                            {/* Error Message */}
                            {submitError && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                                    <div className="flex items-start gap-3">
                                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                                        <div>
                                            <h3 className="font-semibold text-red-900 dark:text-red-300">Error</h3>
                                            <p className="text-sm text-red-700 dark:text-red-400 mt-1">{submitError}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Password Input */}
                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    New Password <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <input
                                        type={formData.showPassword ? 'text' : 'password'}
                                        value={formData.newPassword}
                                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                                        placeholder="Enter new password"
                                        className="w-full px-3 py-2 pr-10 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        disabled={isSubmitting}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setFormData({ ...formData, showPassword: !formData.showPassword })}
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                                    >
                                        {formData.showPassword ? '🙈' : '👁️'}
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Password must be at least 6 characters long
                                </p>
                            </div>

                            {/* Password strength indicator */}
                            {formData.newPassword && (
                                <div className="mb-4">
                                    <div className="flex items-center gap-2">
                                        <div className={`h-1 flex-1 rounded ${formData.newPassword.length >= 6 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                                        <span className={`text-xs ${formData.newPassword.length >= 6 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                            {formData.newPassword.length >= 6 ? 'Valid length' : `${6 - formData.newPassword.length} more characters needed`}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-4">
                                <div className="flex items-start gap-2">
                                    <Info className="w-4 h-4 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                                    <p className="text-xs text-yellow-700 dark:text-yellow-400">
                                        Make sure to communicate the new password to the employee securely. They should change it after logging in.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 border-t dark:border-gray-700 flex justify-end gap-2">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={isSubmitting || !formData.newPassword || formData.newPassword.length < 6}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        <span>Resetting...</span>
                                    </>
                                ) : (
                                    'Reset Password'
                                )}
                            </button>
                        </div>
                    </div>
                );

            case 'withhold-salary': {
                const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                const submitHold = async () => {
                    if (!holdReason.trim()) { setSubmitError('Please provide a reason — payroll holds are audited.'); return; }
                    setSubmitError(null);
                    setIsSubmitting(true);
                    try {
                        await payrollService.createPeriodHold({
                            employeeId: employee.id,
                            month: holdMonth,
                            year: holdYear,
                            reason: holdReason.trim(),
                        });
                        setHoldSuccess(`${MONTHS[holdMonth - 1]} ${holdYear}`);
                        setHoldReason('');
                    } catch (e) {
                        setSubmitError(e?.response?.data?.error || e?.message || 'Could not create hold');
                    } finally {
                        setIsSubmitting(false);
                    }
                };
                const releaseHold = async (holdId) => {
                    setSubmitError(null);
                    setIsSubmitting(true);
                    try {
                        await payrollService.releasePeriodHold(holdId);
                        setActiveHolds((arr) => arr.filter((h) => h.id !== holdId));
                    } catch (e) {
                        setSubmitError(e?.response?.data?.error || e?.message || 'Could not release hold');
                    } finally {
                        setIsSubmitting(false);
                    }
                };
                const years = [today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1];
                return (
                    <div className="flex flex-col h-full">
                        <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center">
                                    <Ban className="w-5 h-5" />
                                </div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                    Withhold salary
                                </h2>
                            </div>
                            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-semibold">
                                    {employee?.first_name?.[0]}{employee?.last_name?.[0]}
                                </div>
                                <div>
                                    <div className="font-medium text-gray-900 dark:text-white">
                                        {employee?.first_name} {employee?.last_name}
                                    </div>
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {employee?.employee_code || employee?.email}
                                    </div>
                                </div>
                            </div>

                            <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-900 dark:text-amber-200">
                                <div className="flex items-start gap-2">
                                    <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <p>
                                        Holding salary for a specific month makes the payslip for that month
                                        show as <b>Salary held</b>. The next month's payroll runs normally.
                                        You can release a hold at any time before payroll for that month is locked.
                                    </p>
                                </div>
                            </div>

                            {/* Active holds */}
                            {activeHolds.length > 0 && (
                                <div>
                                    <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Active holds</div>
                                    <div className="space-y-2">
                                        {activeHolds.map((h) => (
                                            <div key={h.id} className="flex items-center justify-between rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 px-3 py-2">
                                                <div className="text-sm">
                                                    <div className="font-semibold text-red-900 dark:text-red-300">
                                                        {MONTHS[(h.month || 1) - 1]} {h.year}
                                                    </div>
                                                    <div className="text-xs text-red-700 dark:text-red-400 mt-0.5">{h.reason}</div>
                                                </div>
                                                <button
                                                    onClick={() => releaseHold(h.id)}
                                                    disabled={isSubmitting}
                                                    className="px-3 py-1.5 text-xs font-semibold rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-60"
                                                >
                                                    Release
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {holdSuccess && (
                                <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3 text-sm text-green-900 dark:text-green-300 flex items-start gap-2">
                                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <span>Hold added for <b>{holdSuccess}</b>. Their payslip will show "Salary held" when payroll runs for that month.</span>
                                </div>
                            )}

                            {submitError && (
                                <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-900 dark:text-red-300 flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <span>{submitError}</span>
                                </div>
                            )}

                            <div>
                                <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Hold which payroll period?</div>
                                <div className="grid grid-cols-2 gap-2">
                                    <select
                                        value={holdMonth}
                                        onChange={(e) => setHoldMonth(parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                    >
                                        {MONTHS.map((m, idx) => (
                                            <option key={idx + 1} value={idx + 1}>{m}</option>
                                        ))}
                                    </select>
                                    <select
                                        value={holdYear}
                                        onChange={(e) => setHoldYear(parseInt(e.target.value))}
                                        className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                                    >
                                        {years.map((y) => (
                                            <option key={y} value={y}>{y}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Reason <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    value={holdReason}
                                    onChange={(e) => setHoldReason(e.target.value)}
                                    rows={4}
                                    placeholder="e.g. Pending clearance from previous employer / disciplinary review under way."
                                    className="w-full px-3 py-2 border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white resize-y"
                                />
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Visible to admins on the audit trail and inside the payroll run.
                                </p>
                            </div>
                        </div>

                        <div className="p-4 border-t dark:border-gray-700 flex items-center justify-end gap-2 bg-gray-50 dark:bg-gray-900/30">
                            <button
                                onClick={onClose}
                                disabled={isSubmitting}
                                className="px-4 py-2 border dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-700 disabled:opacity-60"
                            >
                                Close
                            </button>
                            <button
                                onClick={submitHold}
                                disabled={isSubmitting || !holdReason.trim()}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 disabled:opacity-60 flex items-center gap-2"
                            >
                                <IndianRupee className="w-4 h-4" />
                                {isSubmitting ? 'Saving…' : 'Hold salary'}
                            </button>
                        </div>
                    </div>
                );
            }

            default:
                return null;
        }
    };

    return (
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
                onClick={onClose}
            />

            {/* Side Panel */}
            <div className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white dark:bg-gray-800 shadow-2xl z-50 transform transition-transform duration-300">
                {renderPanel()}
            </div>
        </>
    );
};

export default EmployeeActionPanels;
